import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { heading3Styles } from '@brightspace-ui/core/components/typography/styles.js';
import { querySelectorComposed } from '@brightspace-ui/core/helpers/dom.js';
import { ProviderMixin } from '@brightspace-ui/core/mixins/provider-mixin';
import * as sentry from '@sentry/browser';
import { css, html, LitElement, nothing } from 'lit';
import { router } from 'lit-element-router';
import yn from 'yn';

import { makePlaywrightTestId, PWT_GENERATED_TEST_ID_CATEGORIES } from '../shared/helpers/playwright.js';
import { ALL_LANG_KEYS, localizerReady, setLocalizer, SUPPORTED_LANG_KEYS } from '../shared/l10n/localize.js';
import { setDocumentLocaleSettings } from '../shared/lib/common-override.js';
import Client from './app-client.js';
import routes from './app-routes.js';
import Session from './app-session.js';
import { initFreshDeskWidget } from './main/lib/chat-widget-initializer/chat-widget-initializer.js';
import { FrontendLocalizer } from './shared/l10n/frontend-localizer/frontend-localizer.js';
import { initGTMWidget } from './shared/lib/gtm/gtm.js';
import { PLG_HOW_IT_WORKS_VISITED_SETTING, resolvePlgLandingDecision } from './shared/lib/plg-landing.js';
import { LocalizeNova } from './shared/mixins/localize-nova/localize-nova.js';
import { NovaNavMixin } from './shared/mixins/nova-nav/nova-nav.js';
import { Actions } from './Store/constants.js';
import { store } from './Store/store.js';

import '@brightspace-ui/core/components/alert/alert-toast.js';
import '@brightspace-ui/core/components/typography/typography.js';
import '@brightspace-ui/core/components/colors/colors.js';

export class App extends LocalizeNova(ProviderMixin(router(NovaNavMixin(LitElement)))) {

  static get properties() {
    return {
      _error: { type: String },
      _loading: { type: Boolean },
    };
  }

  static get styles() {
    return [
      heading3Styles,
      css`
        :host {
          display: flex;
          flex-flow: column nowrap;
          height: 100%;
          justify-content: space-between;
          min-height: 100vh;
          width: 100%;
        }

        main {
          flex: 1 1 auto;
          outline: none;
          width: 100%;
        }

        .immersive {
          height: 100%;
          width: 100%;
        }

        .default {
          margin: 0 auto;
          max-width: 1170px;
          padding: 1.5rem 1.5rem 0 1.5rem;
          width: calc(100% - 3rem);
        }

        .admin {
          display: flex;
          flex: 1;
          flex-flow: column nowrap;
          margin: 0 auto;
          max-width: 1170px;
          padding: 5px 1.5rem 0 1.5rem;
          width: calc(100% - 3rem);
        }

        .d2l-skipnav {
          background-color: var(--d2l-color-tungsten);
          border: 2px solid var(--d2l-color-ferrite);
          border-radius: 0 0 0.8rem 0.8rem;
          color: var(--d2l-wave-white);
          cursor: pointer;
          height: auto;
          margin: 0;
          opacity: 0.8;
          outline: none;
          padding: 0.5rem 3rem;
          position: absolute;
          right: 50%;
          top: -1000px;
          transform: translateX(50%);
          -webkit-transition: top 10s ease-out;
          transition: top 10s ease-out;
          width: fit-content;
          z-index: 100;
        }

        .d2l-skipnav:active, .d2l-skipnav:focus {
          top: 0;
          -webkit-transition: none;
          transition: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .d2l-skipnav {
            -webkit-transition: none;
            transition: none;
          }
        }

        @media (max-width: 615px) {
          .default {
            padding: 1.2rem 0.9rem 0;
            width: calc(100% - 1.8rem);
          }

          .admin {
            margin-left: 0;
            padding: 1.2rem 0.9rem 0 0;
            width: calc(100% - 1.8rem);
          }
        }
`,
    ];
  }

  constructor() {
    super();

    const session = new Session();
    const client = new Client({ session });

    this.provideInstance('d2l-nova-session', session);
    this.provideInstance('d2l-nova-client', client);

    this.session = session;
    this.client = client;
    this._routeInfo = {};
    this._lazyComponentsLoaded = false;
    this._lazyComponentsLoading = false;
  }

  async _loadLazyComponents() {
    if (this._lazyComponentsLoaded) return;
    this._lazyComponentsLoaded = true;

    await Promise.all([
      import('./shared/components/general/app-header/app-header.js'),
      import('./shared/components/general/app-footer/app-footer.js'),
      import('./shared/components/dialog/readonly-dialog/readonly-dialog.js'),
      import('./main/components/general/braze/braze-content-card-container/braze-content-card-container.js'),
      import('./shared/components/general/trial-cta-banner/trial-cta-banner.js'),
      this.session.hasFeature('marketing') ? import('./shared/components/general/marketing-call-to-action/marketing-call-to-action.js') : Promise.resolve(),
    ]);

    this.requestUpdate();
  }

  static get routes() {
    return routes;
  }

  connectedCallback() {
    this._loading = true;
    super.connectedCallback();
    document.addEventListener('nova-details-loaded', async() => {
      this.style.setProperty('--d2l-branding-primary-color', this.session.appDetails.accentColour);
    });

    document.addEventListener('nova-ready', async() => {
      await this.setLanguage();
      // Amplitude setup is async now but we don't await it to not block
      this.setupAmplitude();
      // Sentry setup is async - don't await to avoid blocking
      this.setupSentry();
      const { query } = this._routeInfo;

      let isVerified = false;
      if (this.session.isFromAuth0) {
        isVerified = yn(this.session.user?.getExtraAttribute('emailVerified'));
        if (!isVerified) {
          if (query?.code === 'success' && yn(query?.success)) {
            this.session.user.setExtraAttribute('emailVerified', true);
            this.client.setUser(this.session.user);
            isVerified = yn(this.session.user?.getExtraAttribute('emailVerified'));

            if (isVerified) {
              this.navigateWithoutHistory('/activities');
            }
          }
        }

        if (!isVerified) {
          this.navigateWithoutHistory('/thankyou-signup');
        }
      }

      if (this.session?.user?.tenantId) {
        const tenant = await this.client.fetchTenant(this.session.tenantId);
        store.setState(Actions.TENANT, tenant);
        this.client.features = await this.client.getFeatures(this.session.tenantId);
        if (tenant?.accentColour) this.style.setProperty('--d2l-branding-primary-color', tenant.accentColour);
        const isVerifiedUser = this.session.isPublicPortal ? !this.session.isPublicPortalGuest && this.session.isFromAuth0 && isVerified : true;
        const isPlgTenant = tenant?.isPlgTenant?.() === true;

        if ((tenant.type !== 'provider' && isVerifiedUser)) initFreshDeskWidget(this.session.user, isPlgTenant);

        if (await this._handlePlgLandingRedirects(tenant)) {
          return;
        }
      }
      if (this.session.hasFeature('marketing')) {
        initGTMWidget ();
      }
      this._loading = false;
      await this._setView();

      if (this.session._user?.email) {
        const response = await this.client.getExtraUserInfoRequired(this.session._user.email);
        if (response.extraInfoRequired) this.navigateWithoutHistory('/login/get-user-details');
      }

      if (this.session.loginAttributes?.length > 0) {
        const relayState = this.session.needsOnboarding
          ? '/onboarding'
          : this._routeInfo?.query?.relayState || window.location.pathname;
        this.navigateWithoutHistory(`/login-details?relayState=${relayState}`);
      }
    });

    document.addEventListener('nova-error-loading', async e => {
      this._loading = false;
      if (e.detail?.status === 503) {
        this.navigateWithoutHistory('/maintenance');
        this._setView();
      } else {
        this.navigate('/error');
      }
    });

    document.addEventListener('nova-error', async e => {
      const message = e.detail.message || this.localize('app-nova.catchAllErrorMessage');
      const type = e.detail.type || 'critical';
      this._openErrorToast(message, type, e.detail.noAutoClose, e.detail.buttonText);
    });

    document.addEventListener('nova-hide-freshchat-widget', () => {
      const sheet = document.getElementById('freshchat-style-sheet').sheet;
      if (sheet.rules.length > 0) {
        return;
      }

      const style = `#fc_frame {
        display: none;
      }`;
      sheet.insertRule(style, 0);
    });

    document.addEventListener('nova-show-freshchat-widget', () => {
      const sheet = document.getElementById('freshchat-style-sheet').sheet;
      for (let i = 0; i < sheet.rules.length; i++) {
        sheet.deleteRule(i);
      }
    });

    document.addEventListener('nova-change-language', async e => {
      await this.setLanguage(e.detail.language);
    });
  }

  render() {
    if (this._loading || !this.view) return nothing;

    if (!this._lazyComponentsLoaded && !this._lazyComponentsLoading) {
      // eslint-disable-next-line lit/no-this-assign-in-render
      this._lazyComponentsLoading = true;
      requestAnimationFrame(() => this._loadLazyComponents());
    }

    const layout = this._resolveDataLayout();
    const noMargin = this.view.tagName === 'VIEW-HOME' || layout === 'admin';
    return html`
      ${this._skipNavTemplate()}
      ${this._showDefaultView && this._lazyComponentsLoaded ? html`<app-header></app-header>` : nothing}
      ${this._lazyComponentsLoaded ? this._marketingC2A() : nothing}
      ${this._showDefaultView && this._lazyComponentsLoaded ? html`<trial-cta-banner .user=${this.session.user}></trial-cta-banner>` : nothing}
      <main class=${layout} id="mainContainer">
        ${this.view}
        ${this._alertTemplate()}
        ${this._lazyComponentsLoaded ? html`<readonly-dialog></readonly-dialog>` : nothing}
        ${this._lazyComponentsLoaded ? html`<braze-content-card-container></braze-content-card-container>` : nothing}
      </main>
      ${(this._showDefaultView && this._lazyComponentsLoaded && this.view.tagName !== 'THANKYOU-SIGNUP' && this.view.tagName !== 'AUTH-ERROR' && this.view.tagName !== 'VIEW-ONBOARDING') ? html`<app-footer ?no-margin=${noMargin}></app-footer>` : nothing}
    `;
  }

  async router(route, params, query, data) {
    const layout = this._resolveDataLayout(data);

    const newRouteInfo = { route, params, query, data, layout };
    if (JSON.stringify(this._routeInfo) === JSON.stringify(newRouteInfo)) return;
    const paramsQueryUpdatedOnly = newRouteInfo.route === this._routeInfo.route &&
      (
        JSON.stringify(this._routeInfo.params) !== JSON.stringify(newRouteInfo.params) ||
        JSON.stringify(this._routeInfo.query) !== JSON.stringify(newRouteInfo.query)
      );
    this._routeInfo = newRouteInfo;
    if (paramsQueryUpdatedOnly) {
      this.view.params = this._routeInfo.params;
      this.view.query = this._routeInfo.query;
    } else {
      await this._setView();
    }
  }

  async setLanguage(targetLang = undefined) {
    let language = targetLang;
    if (!language) {
      const browserLanguage = navigator.languages ? navigator.languages[0] : navigator.language;
      language = this._determineSupportedLanguage(browserLanguage);
      if (this.session.user.hasSetting('language')) {
        language = this.session.user.getSetting('language');
      } else if (this._showDefaultView) {
        this.client.setUserSetting('language', language);
      }
    }

    setLocalizer(language, FrontendLocalizer);
    /* Wait for resources to be loaded before setting the document title, otherwise it won't have translations and will result in a
       blank title for the login page
       This is a bit hacky, other solutions welcome
       REF: https://skillswave.atlassian.net/browse/ENG-231
    */
    await localizerReady();

    // const language = 'en'; // Keeping this here to easily revert if something blows up
    const timezoneIdentifier = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setDocumentLocaleSettings(language, timezoneIdentifier);
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('data-timezone', JSON.stringify({ name: this._prettyPrintTimezone(timezoneIdentifier), 'identifier': timezoneIdentifier }));

    // If user came from Auth0, update its language in Auth0 too
    if (this.session.isFromAuth0) {
      const auth0Id = this.session.user.getExtraAttribute('id');
      await this.client.updateAuth0User(auth0Id, { 'lang': language });
    }

    if (targetLang) {
      await this.client.setUserSetting('language', targetLang);
      window.location.reload();
    }
  }

  async setupSentry() {
    try {
      const additionalLoggingIntegration = {
        name: 'additionalLoggingIntegration',
        processEvent: sentryEvent => {
          sentryEvent.extra = {
            ...sentryEvent.extra,
            sessionId: this.session.user.id,
          };
          return sentryEvent;
        },
      };
      const { dsn, sampleRate, tracesSampleRate } = await this.client.retrieveSentryConfig();

      if (!dsn) {
        throw new Error('No Sentry DSN found');
      }

      sentry.init({
        dsn,
        tunnel: '/api/sentry/tunnel',
        integrations: [
          sentry.browserTracingIntegration(),
          sentry.captureConsoleIntegration({ levels: ['error'] }),
          additionalLoggingIntegration,
        ],
        sampleRate,
        tracesSampleRate,
      });
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error setting up Sentry: ', error.message);
      } else {
        console.error(error);
      }
    }
  }

  setupAmplitude() {
    if (this.session.appDetails.amplitudeEnabled) {
      const options = {
        defaultTracking: true,
      };

      if (this.session.appDetails.amplitudeProxyUrl) {
        options.serverUrl = this.session.appDetails.amplitudeProxyUrl;
      }

      const sessionReplayTracking = sessionReplayPlugin({
        sampleRate: this.session?.tenant?.hasTag('plgTenant') ? 1.0 : 0.5,
      });

      amplitude.add(sessionReplayTracking);

      amplitude.init(
        this.session.appDetails.amplitudeKey,
        this.session.user.guid,
        options
      );
    }
  }

  _alertButtonClicked() {
    const buttonEvent = new CustomEvent('toast-button-press', {
      bubbles: true,
      composed: true,
    });
    document.dispatchEvent(buttonEvent);
  }

  _alertTemplate() {
    const playwrightId = makePlaywrightTestId(PWT_GENERATED_TEST_ID_CATEGORIES.toastText, this._error);
    return html`
      <d2l-alert-toast id="toast" type="critical" data-testid="${playwrightId}" no-auto-close button-text @d2l-alert-toast-button-press=${this._alertButtonClicked}>
        ${this._error}
      </d2l-alert-toast>
    `;
  }

  _captureException(error) {
    sentry.captureException(error);
  }

  _closeErrorToast() {
    const toast = this.shadowRoot.getElementById('toast');
    if (toast) {
      toast.open = false;
    }
  }

  _determineSupportedLanguage(languageCode) {
    let languageKeys;
    if (this.session.hasFeature('showUnApprovedLanguages')) {
      languageKeys = ALL_LANG_KEYS;
    } else {
      languageKeys = SUPPORTED_LANG_KEYS;
    }
    const lowerCaseLangCode = languageCode.toLocaleLowerCase();
    if (languageKeys.includes(lowerCaseLangCode)) {
      return lowerCaseLangCode;
    }
    // If specific lang with subtag not found (e.g., 'fr-ca'), then check primary tag (e.g., just 'fr')
    const primaryLang = lowerCaseLangCode.split('-')[0];
    if (languageKeys.includes(primaryLang)) {
      return primaryLang;
    }
    return 'en';
  }

  _marketingC2A() {
    if (this.session.loggedIn && this.session.hasFeature('marketing')) {
      return html`
        <marketing-call-to-action
          .removeMargin="${this._routeInfo.route === 'view-activity'}"
          .learnerTerminology="${this.session.tenant.learnerTerminology}">
        </marketing-call-to-action>`;
    }
    return nothing;
  }

  _openErrorToast(text, type, noAutoClose = true, buttonText = undefined) {
    this._error = text;
    const toast = this.shadowRoot.getElementById('toast');
    if (toast) {
      toast.open = true;
      toast.type = type;
      toast.noAutoClose = noAutoClose;
      toast.buttonText = buttonText;
    }
  }

  get _showDefaultView() {
    return this.session.loggedIn && this._resolveDataLayout() !== 'immersive';
  }

  _hardRedirect(path) {
    window.location.href = path;
  }

  async _handlePlgLandingRedirects(tenant) {
    const { redirectPath, shouldMarkHowItWorksVisited } = resolvePlgLandingDecision({
      tenant,
      user: this.session.user,
      currentPath: window.location.pathname,
    });

    if (redirectPath) {
      this._hardRedirect(redirectPath);
      return true;
    }

    if (shouldMarkHowItWorksVisited) {
      try {
        await this.client.setUserSetting(PLG_HOW_IT_WORKS_VISITED_SETTING, true);
      } catch (error) {
        console.error(error);
      }
    }

    return false;
  }

  _resolveDataLayout(data = undefined) {
    if (data) {
      return data.layout || 'default';
    }

    if (this._routeInfo.data?.getDynamicLayout) {
      return this._routeInfo.data.getDynamicLayout(this.session);
    }

    if (this._routeInfo.data?.layout) {
      return this._routeInfo.data.layout;
    }

    if (this._routeInfo.layout) {
      return this._routeInfo.layout;
    }

    return 'full-width';
  }

  async _setView() {
    if (this._loading) return;
    this._closeErrorToast();

    const { route, params, query, data } = this._routeInfo;
    const publicPortalEnabled = this.session.appDetails?.publicPortalEnabled ?? false;
    if (data.protected(this.session)) {
      if (publicPortalEnabled) {
        const loginLink = await this.client.createGuestLogin({ relayState: this.session.relayState });
        const url = new URL(loginLink.magicLink);
        await this.session.verifyMagicLink(url.searchParams.get('magic'));
        await this.session.loadSession();
      } else {
        this.session.relayState = `${window.location.pathname}${window.location.search}`;
        this.navigateWithoutHistory('/login');
        return;
      }
    }

    const applyParamsToUrl = url => {
      if (!params) return url;
      return Object.keys(params).reduce((acc, key) => acc.replace(`:${key}`, params[key]), url);
    };

    if (data?.type === 'redirect') {
      this.navigateWithoutHistory(applyParamsToUrl(data.url));
      return;
    }

    if (publicPortalEnabled) this.client.setMetaTag('robots', 'index, follow');

    let View;
    try {
      if (data.importDynamicView) {
        const module = await data.importDynamicView(this.session);
        View = module.default;
      } else if (data.importView) {
        const module = await data.importView();
        View = module.default;
      } else if (data.getDynamicView) {
        View = data.getDynamicView(this.session);
      } else {
        View = data.View;
      }
    } catch (error) {
      console.error('Failed to load view:', error);
      this._captureException(error);

      // Prevent infinite loop if the error page itself fails to load (e.g. network down)
      if (route !== 'error') {
        this.navigateWithoutHistory('/error');
      }
      return;
    }

    this.view = new View();

    // If lang key not provided or lang key not found, default to app-nova title
    let localizedTitle = this.localize(data.documentTitleLangKey);
    if (!localizedTitle) localizedTitle = this.localize('app-nova.documentTitle');
    this.client.setDocumentTitle(localizedTitle);

    Object.assign(this.view, { route, params, query });
    window.scrollTo(0, 0); // Individual views may want to control this instead some day
    this.update();
  }

  /**
   * Provides accessibility/screen-reader users the ability to skip navigation menu (header)
   * and go directly to the main content of the page.
   * Location in host html is the first DOM element.
   * TODO: Don't place back into app-nova render until all views in app-routes.js have a focusable element without a shadowRoot.
   */
  _skipNavTemplate() {
    return html`
      <button @click=${this._handleSkipNav} class="d2l-skipnav d2l-heading-3">${this.localize('app-nova.skipToMain')}</button>
    `;
  }

  _handleSkipNav() {
    const elem = querySelectorComposed(document, 'main') ||
			querySelectorComposed(document, '[role="main"]') ||
			querySelectorComposed(document, 'h1');
    if (elem) {
      elem.tabIndex = -1;
      elem.focus();
    } else {
      console.warn('No main content found to skip to');
    }
  }

  _prettyPrintTimezone(timezone) {
    const [region, city] = timezone?.split('/') || [null, null];
    if (!region || !city) {
      return timezone;
    }

    return `${region} - ${city}`;
  }
}

window.customElements.define('app-nova', App);
